import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../../components/Navbar';
import Footer from '../../../home/components/Footer';

export default function RCDTestingPage() {
  useEffect(() => {
    // Set page title and meta description
    document.title = 'RCD Testing Watford | Beyond Electrical';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Regular RCD testing to ensure electrical protection devices work correctly in Watford. Safety testing & certification. NICEIC approved. Call 020 3198 1818.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Regular RCD testing to ensure electrical protection devices work correctly in Watford. Safety testing & certification. NICEIC approved. Call 020 3198 1818.';
      document.head.appendChild(meta);
    }

    // Add LocalBusiness JSON-LD schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Beyond Electrical Services",
      "image": "https://beyondelectrical.co.uk/logo.png",
      "url": "https://beyondelectrical.co.uk/services/safety-compliance/rcd-testing",
      "telephone": "+442031981818",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "",
        "addressLocality": "Watford",
        "addressRegion": "Hertfordshire",
        "postalCode": "",
        "addressCountry": "GB"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 51.6565,
        "longitude": -0.3957
      },
      "areaServed": {
        "@type": "City",
        "name": "Watford"
      },
      "serviceType": "RCD Testing",
      "priceRange": "££",
      "openingHours": "Mo-Su 00:00-23:59"
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const areasServed = [
    'Watford Town Centre',
    'Oxhey',
    'Bushey',
    'Croxley Green',
    'Abbots Langley',
    'Rickmansworth',
    'Garston',
    'North Watford',
    'West Watford',
    'Carpenders Park',
    'Leavesden',
    'Nascot Wood',
    'Holywell',
    'Meriden',
    'Cassiobury'
  ];

  const whatsIncluded = [
    {
      icon: '⚡',
      title: 'RCD Trip Time Testing',
      description: 'Precise testing to ensure RCDs disconnect within 40ms at rated current'
    },
    {
      icon: '🔍',
      title: 'Sensitivity Testing',
      description: 'Verification that RCDs trip at correct current levels (30mA for socket circuits)'
    },
    {
      icon: '🔧',
      title: 'All RCD Types',
      description: 'Testing of standard RCDs, RCBOs, and main switch RCDs in consumer units'
    },
    {
      icon: '📋',
      title: 'Test Certification',
      description: 'Official documentation of RCD test results and compliance status'
    },
    {
      icon: '🛠️',
      title: 'Fault Identification',
      description: 'Detection of faulty or slow-tripping RCDs that need replacement'
    },
    {
      icon: '✅',
      title: 'BS 7671 Compliance',
      description: 'Testing to current wiring regulations with professional certification'
    }
  ];

  const benefits = [
    {
      icon: '⚡',
      title: 'Electric Shock Protection',
      description: 'Ensure RCDs provide life-saving protection against electric shocks'
    },
    {
      icon: '🔥',
      title: 'Fire Prevention',
      description: 'Working RCDs prevent electrical fires from earth faults'
    },
    {
      icon: '⚖️',
      title: 'Legal Requirement',
      description: 'Regular RCD testing required for landlords and businesses'
    },
    {
      icon: '🏠',
      title: 'Peace of Mind',
      description: 'Know your electrical safety devices are working properly'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] via-[#1a2942] to-[#0A1628]"></div>
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://public.readdy.ai/ai/img_res/0437e4986e7de800cb854563bf0313ae.jpg" 
            alt="RCD Testing Background" 
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
              <span className="text-white text-sm font-medium">🏆 NICEIC Approved</span>
            </div>
            <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
              <span className="text-white text-sm font-medium">📍 Watford</span>
            </div>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            RCD Testing<br />
            <span className="text-[#D4AF37]">in Watford</span>
          </h1>
          
          <p className="text-xl text-gray-200 mb-8 max-w-2xl leading-relaxed">
            Professional RCD testing to ensure your electrical safety devices are working correctly. Essential testing for landlords, homeowners, and businesses to prevent electric shocks and fires.
          </p>
          
          <div className="flex flex-wrap gap-4">
            <a 
              href="tel:02031981818"
              className="bg-[#D4AF37] text-[#0A1628] px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#C5A028] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-phone-line"></i>
              Call 020 3198 1818
            </a>
            <Link 
              to="/#contact"
              className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/20 transition-all duration-300 border border-white/30 whitespace-nowrap"
            >
              Get Free Quote
            </Link>
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#0A1628] mb-4">Complete RCD Testing Service</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional testing to verify your RCDs provide proper electrical safety protection
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whatsIncluded.map((item, index) => (
              <div 
                key={index}
                className="bg-white p-8 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-[#0A1628] to-[#1a2942] rounded-lg mb-6 text-3xl">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold text-[#0A1628] mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#0A1628] mb-4">Why RCD Testing Matters</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Critical safety testing to ensure your RCDs work when you need them most
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="text-center p-6 rounded-xl hover:bg-gray-50 transition-all duration-300"
              >
                <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#D4AF37] to-[#C5A028] rounded-full mb-6 text-4xl mx-auto shadow-lg">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold text-[#0A1628] mb-3">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gradient-to-br from-[#0A1628] via-[#1a2942] to-[#0A1628] text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Why Choose Beyond Electrical?</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Watford's trusted experts for professional RCD testing
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            <div className="text-center p-6">
              <div className="w-16 h-16 flex items-center justify-center bg-[#D4AF37] rounded-full mb-4 text-3xl mx-auto">
                <i className="ri-shield-check-line"></i>
              </div>
              <h3 className="text-lg font-bold mb-2">NICEIC Approved</h3>
              <p className="text-gray-300 text-sm">Certified RCD testing specialists</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 flex items-center justify-center bg-[#D4AF37] rounded-full mb-4 text-3xl mx-auto">
                <i className="ri-time-line"></i>
              </div>
              <h3 className="text-lg font-bold mb-2">Fast Service</h3>
              <p className="text-gray-300 text-sm">Quick testing with minimal disruption</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 flex items-center justify-center bg-[#D4AF37] rounded-full mb-4 text-3xl mx-auto">
                <i className="ri-file-list-3-line"></i>
              </div>
              <h3 className="text-lg font-bold mb-2">Full Certification</h3>
              <p className="text-gray-300 text-sm">Official test certificates provided</p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 flex items-center justify-center bg-[#D4AF37] rounded-full mb-4 text-3xl mx-auto">
                <i className="ri-customer-service-2-line"></i>
              </div>
              <h3 className="text-lg font-bold mb-2">Expert Repairs</h3>
              <p className="text-gray-300 text-sm">Replace faulty RCDs on the spot</p>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 max-w-4xl mx-auto">
            <div className="flex items-center gap-6">
              <div className="w-24 h-24 flex items-center justify-center bg-[#D4AF37] rounded-full text-4xl flex-shrink-0">
                🏆
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-2">RCD Testing Specialists</h3>
                <p className="text-gray-300 leading-relaxed">
                  Our NICEIC approved electricians use specialist testing equipment to verify your RCDs are working correctly. We test trip times, sensitivity, and overall performance to BS 7671 standards.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Areas Covered Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#0A1628] mb-4">Areas We Cover</h2>
            <p className="text-xl text-gray-600">
              RCD testing services across Watford and surrounding areas
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            {areasServed.map((area, index) => (
              <span 
                key={index}
                className="px-6 py-3 bg-white rounded-full text-[#0A1628] font-medium border border-gray-200 hover:border-[#D4AF37] hover:bg-[#D4AF37] hover:text-white transition-all duration-300 cursor-pointer shadow-sm hover:shadow-md"
              >
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#0A1628] via-[#1a2942] to-[#0A1628] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Need RCD Testing?
          </h2>
          <p className="text-xl text-gray-300 mb-8 leading-relaxed">
            Ensure your electrical safety devices are working properly with professional RCD testing from NICEIC approved electricians in Watford.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <a 
              href="tel:02031981818"
              className="bg-[#D4AF37] text-[#0A1628] px-8 py-4 rounded-lg font-semibold text-lg hover:bg-[#C5A028] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-phone-line"></i>
              Call 020 3198 1818
            </a>
            <Link 
              to="/#contact"
              className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/20 transition-all duration-300 border border-white/30 whitespace-nowrap"
            >
              Request Callback
            </Link>
          </div>

          <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-300">
            <div className="flex items-center gap-2">
              <i className="ri-check-line text-[#D4AF37] text-xl"></i>
              <span>Fast Testing</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-check-line text-[#D4AF37] text-xl"></i>
              <span>NICEIC Approved</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-check-line text-[#D4AF37] text-xl"></i>
              <span>Full Certification</span>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}